import { useEffect, useState, useRef } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./contexts/AuthContext";
import { PinLockProvider } from "./contexts/PinLockContext";
import { useSystemSettings } from "./hooks/useSystemSettings";
import { ErrorBoundary } from "./components/feature/ErrorBoundary";
import { initAnalytics, trackPageView, setAnalyticsUser } from "./lib/analytics";
import { PandaAIChat } from "./components/feature/PandaAIChat";

/**
 * Renders PandaAIChat only for user-facing routes.
 * Never shows the floating button inside /admin/* pages.
 */
function ConditionalPandaAI() {
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/admin');
  if (isAdminRoute) return null;
  return <PandaAIChat />;
}

function SystemSettingsInitializer() {
  const { settings, loading } = useSystemSettings();

  useEffect(() => {
    if (loading) return;
    const name = settings.platform_name;
    if (name && document.title !== name) {
      document.title = name;
    }
  }, [settings.platform_name, loading]);

  useEffect(() => {
    const faviconUrl = settings.logo_small_url || settings.logo_url;
    if (!faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [settings.logo_small_url, settings.logo_url]);

  return null;
}

/** Initializes GA4 once settings are loaded and keeps user identity in sync */
function AnalyticsInitializer() {
  const { settings, loading } = useSystemSettings();
  const { user, profile } = useAuth();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (loading || initializedRef.current) return;
    initializedRef.current = true;
    initAnalytics({
      enabled: settings.ga4_enabled === 'true',
      measurementId: settings.ga4_measurement_id || '',
      environment: (settings.ga4_environment as 'production' | 'all') || 'production',
      trackEvents: settings.ga4_track_events !== 'false',
    });
  }, [loading, settings]);

  useEffect(() => {
    setAnalyticsUser(user?.id ?? null, profile?.plan ?? null);
  }, [user?.id, profile?.plan]);

  return null;
}

/** Fires a page_view event on every SPA route change */
function PageTracker() {
  const location = useLocation();
  const [prevPath, setPrevPath] = useState('');

  useEffect(() => {
    if (location.pathname !== prevPath) {
      setPrevPath(location.pathname);
      trackPageView(location.pathname);
    }
  }, [location.pathname, prevPath]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <AuthProvider>
          <PinLockProvider>
            <BrowserRouter basename={__BASE_PATH__}>
              <SystemSettingsInitializer />
              <AnalyticsInitializer />
              <PageTracker />
              <AppRoutes />
              <ConditionalPandaAI />
            </BrowserRouter>
          </PinLockProvider>
        </AuthProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;
